<template>
	<div class="ai-tools-container">
		<ModalHeader :title="getString('ai_x_reader', [userStore.menuWord])" />
		<div class="row upload-row">
			<p>{{ getString('ai_tools_phrase', [userStore.menuWord]) }}</p>
		</div>
		<div class="row animation-row">
			<div id="ai-tools-generation-anim"></div>
			<div class="dpz-div">
				<AppDropzone
					:class="{disabled: loading}"
					class="menu-dpz"
					is-ai
					:max-bytes="5 * 1000 * 1024"
					:max-width="5000"
					:max-height="5000"
					:allow-pdf="true"
					@upload-complete="fileUploadComplete"
					ref="dpz"
				/>
			</div>
		</div>
		<p v-show="loading" class="mt-8">{{ getString('generating_result') }}</p>
		<div class="row space-between result-row" v-if="runResult">
			<p class="title">{{ getString('result') }}</p>
			<p class="data result">{{ runResult }}</p>
		</div>
	</div>
</template>

<script>
import AppDropzone from "@/shared/components/AppDropzone.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import API from "@/shared/helpers/API";
import {useMenuStore, useUserStore} from "@/console-new/stores";
import {useNotificationStore, useLanguageStore, useCurrencyStore} from "@/shared/stores";
import AppLoader from "@/shared/components/AppLoader.vue";
import AppButton from "@/shared/components/AppButton.vue";

export default {
	setup() {
		const userStore = useUserStore();
		const notificationStore = useNotificationStore();
		const menuStore = useMenuStore();
		const currencyStore = useCurrencyStore();
		const languageStore = useLanguageStore();
		return {userStore, currencyStore, showNotification: notificationStore.showNotification, getString: languageStore.getString, menuStore};
	},
	name: "AITools",
	components: {AppButton, ModalHeader, AppDropzone, AppLoader},
	data() {
		return {
			loading: false,
			runResult: null,
			generationAnimation: null,
			loadedAnimation: false,
			noiseResult: null,
			items: [],
		};
	},
	methods: {
		fileUploadComplete(urls) {
			let url = urls[0];
			this.menuStore.aiToolsLoading = true;
			this.generationAnimation.play();

			setTimeout(() => {

				this.loading = true;
				API.init().decodeMenuFile(this.userStore.currBusinessId, url, (response) => {
					if(response && response.result && response.result === "OK" && response.ai_menu_log_id) {
						this.startRunPolling(response.ai_menu_log_id);
					} else if(response && response?.generating?.ai_menu_log_id) {
						this.showNotification("already-generating-menu", [this.userStore.menuWord]);
						this.startRunPolling(response.generating.ai_menu_log_id);
					} else {
						this.stopLoading();
						this.showNotification(response?.details === 'rate_limit' ? "menu-fail-rate-limit" : "generic-fail");
					}
				});

				//this.analyzeResults("Pizze\nMargherita - 8,5\u20ac\nPomodoro, mozzarella, basilico\nNapoletana - 10\u20ac\nPomodoro, mozzarella,\nac", "Pizze | Margherita | 8,5\u20ac | Pomodoro, mozzarella, basilico\nPizze | Napoletana | 10\u20ac | Pomodoro, mozzarella, acciughe, capperi")
			}, 550);
		},
		startRunPolling(aIMenuLogId) {

			API.init().checkMenuRun(this.userStore.currBusinessId, aIMenuLogId, (response) => {
				if(response && response.result && response.result === "OK" && response.run_result) {
					if(response.run_result.status === "failed") {
						this.showNotification("generic-fail");
						this.stopLoading();
					} else if(response.run_result.status === "completed") {
						this.analyzeResults(response.run_result.result);
					} else {
						setTimeout(() => {
							this.startRunPolling(aIMenuLogId);
						}, 3000);
					}
				} else {
					this.showNotification("generic-fail");
					this.stopLoading();
				}
			});

		},
		analyzeResults(runResult) {

			let result = runResult.split("\n");
			let noise = [];
			let items = [];

			for(let i = 0; i < result.length; i++) {
				let line = result[i];
				if(line.includes("|")) {
					let item = line.split("|");
					if(item.length === 4) {
						items.push({
							category: item[0].trim(),
							name: item[1].trim(),
							price: this.parsePrice(item[2].trim()),
							description: item[3].trim(),
						});
					} else {
						noise.push(this.$sanitize(line));
					}
				} else {
					noise.push(this.$sanitize(line));
				}
			}

			this.runResult = this.getString('found_x_items', [items.length]);
			this.noiseResult = noise.join("<br>");
			this.items = items;

			if(items.length > 0) {
				this.addItemsToMenu();
			} else {
				this.stopLoading();
				this.showNotification("menu-generation-empty-items", [this.userStore.menuWord]);
			}
		},
		addItemsToMenu() {

			let categories = {};
			for(let i = 0; i < this.items.length; i++) {
				let item = this.items[i];
				if(!categories[item.category]) {
					let newCategory = this.menuStore.addNewCategory({name: item.category});
					newCategory.editActive = false;
					newCategory.editActiveDelayed = false;
					categories[item.category] = newCategory.tempid;
				}
			}

			this.menuStore.saveCategories((success, details) => {
				if(success) {
					for(let i = 0; i < this.items.length; i++) {
						let item = this.items[i];
						if(categories.hasOwnProperty(item.category)) {
							let newItem = this.menuStore.addNewItem(categories[item.category], {
								name: item.name,
								price: item.price,
								ingredients: item.description,
							});
							newItem.active = false;
							newItem.collapsedDelayed = true;
						}
					}
					this.menuStore.saveItems(()=>{
						this.menuStore.refreshMenusData(()=>{
							this.menuStore.aiToolsActive = false;
							this.stopLoading();
							this.showNotification("menu-generation-success-items", [this.items.length, this.userStore.menuWord]);
						});
					});
				} else {
					this.stopLoading();
					this.showNotification("generic-fail");
				}
			});
		},
		parsePrice(price) {
			let priceString = price.replace(",", ".").replace("€", "").replace("EUR", "").replace("eur", "").replace("$", "").replace("USD", "").replace("usd", "").trim();
			for(let currency in this.currencyStore.currencies) {
				let currencySymbol = this.currencyStore.currencies[currency].symbol;
				let currencyCode = this.currencyStore.currencies[currency].code;
				priceString = priceString.replace(currencySymbol, "").replace(currencyCode, "").replace(currencyCode.toLowerCase(), "").trim();
			}
			let priceFloat = parseFloat(priceString);
			if(isNaN(priceFloat)) {
				return 0;
			} else {
				return Number((priceFloat* 100).toFixed(0));
			}
		},
		loadGeneratingAnimation() {
			if(typeof bodymovin !== 'undefined') {
				const animationPath = 'https://img.qromo.io/img/vue/anim/ai-generation' + (this.userStore.darkMode ? '-dark' : '-light') + '.json';
				if(!this.loadedAnimation) {
					this.generationAnimation = bodymovin.loadAnimation({
						container: document.getElementById('ai-tools-generation-anim'),
						renderer: 'svg',
						loop: true,
						autoplay: false,
						path: animationPath,
						rendererSettings: {
							preserveAspectRatio: 'xMidYMid meet'
						}
					});
					this.loadedAnimation = true;
				}
			}
		},
		stopLoading() {
			this.loading = false;
			this.menuStore.aiToolsLoading = false;
			if(this.generationAnimation) {
				this.generationAnimation.stop();
			}
			if(this.$refs['dpz']) {
				this.$refs['dpz'].dropzoneObj.removeAllFiles()
				this.$refs['dpz'].currentFiles = [];
			}
		},
	},
	mounted() {
		this.loadGeneratingAnimation();
	},
	beforeUnmount() {
		if(this.generationAnimation) {
			this.generationAnimation.destroy();
		}
	}
}
</script>

<style scoped lang="scss">
.upload-row {
	margin-top: 12px;
	margin-bottom: 20px;
	gap: 12px;
}
.result-row {
	border-bottom: 1px solid $primary;
	padding: 8px 0;
	gap: 12px;

	&:last-child {
		border-bottom: none;
	}

	.title {
		font-weight: 500;
		color: #9e9e9e;
		font-size: 14px;
	}
	.data {
		margin-left: auto;
		font-size: 14px;
	}
	.result {
		font-size: 16px;
		font-weight: 700;
	}
}
#ai-tools-generation-anim {
	width: 100%;
	max-width: calc(100% - 300px);
	margin: 0 auto;
	height: fit-content;
	position: relative;
	:deep(svg) {
		height: auto !important;
	}

	@media (max-width: 1080px) {
		max-width: calc(100% - 210px);
	}
}
@media (max-width: 768px) {
	.animation-row {
		@include centered;
		gap: 20px;
		flex-direction: column-reverse;
	}
	#ai-tools-generation-anim {
		max-width: 100%;
	}
	.dpz-div {
		width: 100%;
	}
}
.menu-dpz.dropzone-container {
	height: 100%;
	width: 220px;

	:deep(.dropzone-wrapper) {
		height: 100%;
		width: 100%;
		.dropzone {
			width: 100%;
			height: 100%;
		}
	}
	@media (max-width: 1080px) {
		width: 150px;
	}
	@media (max-width: 768px) {
		width: 100%;
		height: 150px;
	}
}
</style>